import { useEffect, useState } from "react";
import {
  Bike,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Package,
  PackageCheck,
  Store,
  Truck,
  Users,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Button, Pill, SearchBar, type PillVariant } from "@veorest/ui";
import { downloadCsv, formatCurrency, formatOrderDisplayNumber } from "@veorest/utils";
import { SegmentStrip } from "../../data/SegmentStrip";
import { partnerStageTranslationKey, type PartnerOrderStage } from "../../../lib/partnerOrders";
import {
  feeResponsibilityLabelKey,
  formatShortDateTime,
  paymentTypeLabel,
  type ClientOrderRow,
} from "../../../lib/partnerReports";
import { usePartnerClientOrders } from "../../../hooks/usePartnerReports";
import type { PartnerDashboardDateRange } from "../../../lib/partnerDashboardDate";
import { useI18n, type TranslationKey } from "../../../i18n";

interface ClientOrdersTabProps {
  tenantId: string;
  currency: string;
  range: PartnerDashboardDateRange;
}

const PAGE_SIZE = 25;

const STAGE_PILL: Record<PartnerOrderStage, PillVariant> = {
  pending: "warning",
  processing: "info",
  ready: "accent",
  in_delivery: "info",
  completed: "success",
  cancelled: "danger",
};

const STAGE_TABS: Array<{ key: PartnerOrderStage | "all"; icon: LucideIcon }> = [
  { key: "all", icon: Users },
  { key: "pending", icon: Clock },
  { key: "processing", icon: Package },
  { key: "ready", icon: PackageCheck },
  { key: "in_delivery", icon: Bike },
  { key: "completed", icon: CheckCircle2 },
  { key: "cancelled", icon: XCircle },
];

const FULFILLMENT_TABS: Array<{
  key: "all" | "delivery" | "pickup";
  icon: LucideIcon;
  labelKey: TranslationKey;
}> = [
  { key: "all", icon: Users, labelKey: "analytics.all" },
  { key: "delivery", icon: Truck, labelKey: "common.delivery" },
  { key: "pickup", icon: Store, labelKey: "common.pickup" },
];

function escapeCsv(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

export function ClientOrdersTab({ tenantId, currency, range }: ClientOrdersTabProps) {
  const { t, locale } = useI18n();
  const [stage, setStage] = useState<PartnerOrderStage | "all">("all");
  const [fulfillment, setFulfillment] = useState<"all" | "delivery" | "pickup">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const query = usePartnerClientOrders(tenantId, range, {
    stage,
    fulfillment,
    query: search,
    page,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    setPage(0);
  }, [stage, fulfillment, search, range]);

  const data = query.data;
  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const money = (v: number | null) =>
    v == null ? "—" : formatCurrency(v, currency, locale);

  const handleExport = () => {
    const header = [
      t("clientOrders.colDate"),
      t("clientOrders.colOrder"),
      t("clientOrders.colCustomer"),
      t("clientOrders.colDriver"),
      t("clientOrders.colStatus"),
      t("clientOrders.colTotal"),
      t("clientOrders.colDeliveryFee"),
      t("clientOrders.colResponsibility"),
      t("clientOrders.colCharge"),
      t("clientOrders.colPayment"),
      t("clientOrders.colKm"),
    ].join(",");
    const rows = orders.map((order: ClientOrderRow) =>
      [
        escapeCsv(order.created_at ?? ""),
        escapeCsv(formatOrderDisplayNumber(order.id)),
        escapeCsv(order.customer_name),
        escapeCsv(order.driver_name ?? ""),
        escapeCsv(t(partnerStageTranslationKey(order.stage))),
        escapeCsv(String(order.total)),
        escapeCsv(order.delivery_fee == null ? "" : String(order.delivery_fee)),
        escapeCsv(
          order.delivery_fee_responsibility
            ? t(feeResponsibilityLabelKey(order.delivery_fee_responsibility))
            : ""
        ),
        escapeCsv(order.partner_fee_amount == null ? "" : String(order.partner_fee_amount)),
        escapeCsv(t(paymentTypeLabel(order))),
        escapeCsv(order.delivery_km == null ? "" : String(order.delivery_km)),
      ].join(",")
    );
    const csv = [header, ...rows].join("\n");
    downloadCsv(`client-orders-${tenantId.slice(0, 8)}.csv`, `\uFEFF${csv}`);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="filter-bar filter-bar--orders">
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("clientOrders.searchPlaceholder")}
          aria-label={t("clientOrders.searchAria")}
          className="max-w-40"
        />
        <SegmentStrip
          value={stage}
          onChange={(next) => setStage(next)}
          ariaLabel={t("clientOrders.stageFilter")}
          options={STAGE_TABS.map(({ key, icon }) => ({
            key,
            icon,
            label: t(partnerStageTranslationKey(key)),
          }))}
        />
        <SegmentStrip
          value={fulfillment}
          onChange={(next) => setFulfillment(next)}
          ariaLabel={t("clientOrders.fulfillmentFilter")}
          options={FULFILLMENT_TABS.map(({ key, icon, labelKey }) => ({
            key,
            icon,
            label: t(labelKey),
          }))}
        />
        <Button
          variant="secondary"
          onClick={handleExport}
          disabled={orders.length === 0}
          className="ms-auto min-h-[38px] gap-2 px-3"
        >
          <Download className="h-4 w-4" />
          {t("clientOrders.export")}
        </Button>
      </div>

      <section className="menu-panel">
        <div className="menu-panel__body">
          <div className="report-table-wrap">
            <table className="table-flat">
              <thead>
                <tr>
                  <th>{t("clientOrders.colDate")}</th>
                  <th>{t("clientOrders.colOrder")}</th>
                  <th>{t("clientOrders.colCustomer")}</th>
                  <th>{t("clientOrders.colDriver")}</th>
                  <th>{t("clientOrders.colStatus")}</th>
                  <th className="text-end">{t("clientOrders.colTotal")}</th>
                  <th className="text-end">{t("clientOrders.colDeliveryFee")}</th>
                  <th>{t("clientOrders.colResponsibility")}</th>
                  <th className="text-end">{t("clientOrders.colCharge")}</th>
                  <th>{t("clientOrders.colPayment")}</th>
                  <th className="text-end">{t("clientOrders.colKm")}</th>
                </tr>
              </thead>
              <tbody>
                {query.isLoading ? (
                  <tr>
                    <td colSpan={11} className="py-10 text-center text-sm text-[var(--text-muted)]">
                      {t("common.loading")}
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-10 text-center text-sm text-[var(--text-muted)]">
                      {t("clientView.noOrdersRange")}
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id}>
                      <td className="whitespace-nowrap text-sm text-[var(--text-secondary)]">
                        {formatShortDateTime(order.created_at)}
                      </td>
                      <td className="whitespace-nowrap font-mono text-xs">
                        {formatOrderDisplayNumber(order.id)}
                      </td>
                      <td className="whitespace-nowrap font-semibold text-[var(--text-primary)]">
                        {order.customer_name}
                      </td>
                      <td className="whitespace-nowrap text-sm text-[var(--text-secondary)]">
                        {order.driver_name ?? "—"}
                      </td>
                      <td>
                        <Pill variant={STAGE_PILL[order.stage]}>
                          {t(partnerStageTranslationKey(order.stage))}
                        </Pill>
                      </td>
                      <td className="text-end tabular">{money(order.total)}</td>
                      <td className="text-end tabular">{money(order.delivery_fee)}</td>
                      <td className="text-xs text-[var(--text-secondary)]">
                        {order.delivery_fee_responsibility
                          ? t(feeResponsibilityLabelKey(order.delivery_fee_responsibility))
                          : "—"}
                      </td>
                      <td className="text-end tabular">{money(order.partner_fee_amount)}</td>
                      <td className="text-xs text-[var(--text-secondary)]">
                        {t(paymentTypeLabel(order))}
                      </td>
                      <td className="text-end tabular text-sm">
                        {order.delivery_km == null ? "—" : order.delivery_km.toFixed(1)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-[var(--text-muted)]">
              {t("clientOrders.showing", { from, to, total })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || query.isLoading}
                className="min-h-9 gap-1 px-3"
              >
                <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
                {t("clientOrders.prev")}
              </Button>
              <span className="text-xs font-bold text-[var(--text-secondary)]">
                {page + 1} / {pageCount}
              </span>
              <Button
                variant="secondary"
                onClick={() => setPage((p) => (to < total ? p + 1 : p))}
                disabled={to >= total || query.isLoading}
                className="min-h-9 gap-1 px-3"
              >
                {t("clientOrders.next")}
                <ChevronRight className="h-4 w-4 rtl:rotate-180" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
